import React, { useEffect } from 'react';
import { UserCheck, X, Calendar } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import Button from './Button';

interface DeletedUserInfo {
  id: string;
  name: string;
  email?: string;
  phone: string;
  role: string;
  deleted_at?: string;
  deleted_date?: string;
}

interface RestoreUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  deletedUser: DeletedUserInfo | null;
  loading?: boolean;
}

const RestoreUserModal: React.FC<RestoreUserModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  deletedUser,
  loading = false
}) => {
  const { theme } = useAppSelector((state) => state.ui);

  // Handle scroll locking and escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !loading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Lock body scroll
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.removeEventListener('keydown', handleEscape);
        // Restore body scroll
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen, loading, onClose]);

  if (!isOpen || !deletedUser) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (!loading && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleModalScroll = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  const handleModalTouchMove = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  return (
    <div 
      className="fixed inset-0 z-[10000] flex items-center justify-center p-2 sm:p-4"
      onClick={handleOverlayClick}
      onWheel={handleModalScroll}
      onTouchMove={handleModalTouchMove}
    >
      {/* Background overlay */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleOverlayClick}
      />
      
      {/* Modal panel */}
      <div 
        className="relative w-full max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-y-auto overscroll-contain"
        onWheel={handleModalScroll}
        onTouchMove={handleModalTouchMove}
      >
        <div 
          className={`transform overflow-hidden rounded-lg text-left shadow-xl transition-all ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b flex-shrink-0 ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-full ${
                  theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'
                }`}>
                  <UserCheck className={`h-5 w-5 ${
                    theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                </div>
                <h3 className={`text-lg font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Restore Deleted User
                </h3>
              </div>
              <button
                onClick={onClose}
                className={`rounded-md p-1 ${
                  theme === 'dark' 
                    ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
                disabled={loading}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-4 sm:px-6 py-4">
            <div className="text-sm">
              <p className={`mb-4 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                A user with this {deletedUser.email ? 'email' : 'phone number'} was deleted previously. 
                Would you like to restore and update their information?
              </p>
              
              <div className={`p-4 rounded-md border ${
                theme === 'dark' 
                  ? 'bg-gray-900 border-gray-700' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="space-y-2">
                  <div>
                    <p className={`text-xs font-medium mb-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Name
                    </p>
                    <p className={`font-semibold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {deletedUser.name}
                    </p>
                  </div>
                  
                  {deletedUser.email && (
                    <div>
                      <p className={`text-xs font-medium mb-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Email
                      </p>
                      <p className={`${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {deletedUser.email}
                      </p>
                    </div>
                  )}
                  
                  <div>
                    <p className={`text-xs font-medium mb-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Phone
                    </p>
                    <p className={`${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {deletedUser.phone}
                    </p>
                  </div>
                  
                  <div>
                    <p className={`text-xs font-medium mb-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Role
                    </p>
                    <p className={`${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {deletedUser.role.charAt(0).toUpperCase() + deletedUser.role.slice(1)}
                    </p>
                  </div>
                  
                  {deletedUser.deleted_date && (
                    <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-gray-600">
                      <Calendar className={`h-4 w-4 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`} />
                      <div>
                        <p className={`text-xs font-medium mb-1 ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Deleted On
                        </p>
                        <p className={`text-xs ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {deletedUser.deleted_date}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className={`mt-4 p-3 rounded-md ${
                theme === 'dark' 
                  ? 'bg-blue-900/20 border border-blue-800' 
                  : 'bg-blue-50 border border-blue-200'
              }`}>
                <p className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-800'
                }`}>
                  ℹ️ The user will be restored with the information you entered in the form. 
                  Their previous data will be updated accordingly.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className={`px-4 sm:px-6 py-4 border-t flex-shrink-0 ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={onConfirm}
                disabled={loading}
                className="min-w-[120px]"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Restoring...
                  </div>
                ) : (
                  'Restore & Update'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestoreUserModal;
export type { DeletedUserInfo };

